<template>
  <div class="rd-fbar" role="toolbar" :aria-label="$t('report_desktop.filters')" @keydown.esc="closeOnEscape">
    <slot name="before"></slot>

    <ui-segment
      v-if="showPeriod && !periodLocked"
      :value="activeKey"
      :options="quickOptions"
      :label="$t('report_mobile.period')"
      @input="pickQuick"
    />

    <ui-popover
      v-if="showPeriod"
      ref="period"
      panel-class="rd-pop--period"
      :label="$t('report_mobile.period')"
      @open="onOpen('period')"
    >
      <template #trigger="{ attrs, on }">
        <button
          type="button"
          class="ui-btn"
          :class="{ 'ui-btn--accent': !activeKey && !periodLocked, 'rd-btn--static': periodLocked }"
          v-bind="attrs"
          :disabled="periodLocked"
          data-rd-trigger="period"
          v-on="on"
        >
          <v-icon size="16" :color="activeKey || periodLocked ? '#1c1917' : '#9a3412'">mdi-calendar-blank-outline</v-icon>
          <span class="rd-num">{{ periodLabel }}</span>
          <v-icon v-if="!periodLocked" size="16" :color="activeKey ? '#5f5852' : '#9a3412'">mdi-chevron-down</v-icon>
        </button>
      </template>

      <template #default>
        <div class="rd-pop__head">
          <span>{{ $t("report_mobile.period") }}</span>
          <span class="rd-num">{{ draftText }}</span>
        </div>
        <div class="rd-presets">
          <button
            v-for="preset in presets"
            :key="preset.key"
            type="button"
            :aria-pressed="String(activeKey === preset.key)"
            @click="applyPreset(preset)"
          >
            {{ $t(`report_mobile.presets.${preset.key}`) }}
          </button>
        </div>
        <report-calendar dense :start="draft[0] || ''" :end="draft[1] || ''" @pick="pickDay"></report-calendar>
        <div class="rd-pop__links">
          <span class="rd-muted rd-num">{{ draftRange }}</span>
          <button type="button" class="ui-btn ui-btn--primary ui-btn--sm" :disabled="!draft.length" @click="applyDraft">
            {{ $t("report_mobile.apply") }}
          </button>
        </div>
      </template>
    </ui-popover>

    <ui-popover
      v-if="companyMode"
      ref="companies"
      panel-class="rd-pop--companies"
      :label="entityLabels.title"
      @open="onOpen('companies')"
    >
      <template #trigger="{ attrs, on }">
        <button
          type="button"
          class="ui-btn"
          :class="{ 'ui-btn--accent': companyAccent }"
          v-bind="attrs"
          data-rd-trigger="companies"
          v-on="on"
        >
          <span class="rd-bar-label" :title="companiesLabel">{{ companiesLabel }}</span>
          <v-icon size="16" :color="companyAccent ? '#9a3412' : '#5f5852'">mdi-chevron-down</v-icon>
        </button>
      </template>

      <template #default="{ close }">
        <div class="rd-pop__head">
          <span>{{ entityLabels.title }}</span>
          <span v-if="companyMode === 'multi'" class="rd-num">
            {{ $t("report_mobile.selected_of", { selected: selectedIds.length, total: companies.length }) }}
          </span>
        </div>
        <label v-if="companies.length > 7" class="rd-pop__search">
          <v-icon size="16" color="#5f5852">mdi-magnify</v-icon>
          <span class="rd-visually-hidden">{{ entityLabels.search }}</span>
          <input
            ref="companySearch"
            v-model="companyQuery"
            type="search"
            :placeholder="entityLabels.search"
          />
        </label>
        <div class="rd-checklist" role="group" :aria-label="entityLabels.title">
          <label v-for="company in visibleCompanies" :key="company.id">
            <input
              :type="companyMode === 'single' ? 'radio' : 'checkbox'"
              name="rd-company"
              :checked="selectedIds.includes(company.id)"
              @change="pickCompany(company.id)"
            />
            <span>{{ toLatin(company.name) }}</span>
          </label>
          <span v-if="!visibleCompanies.length" class="rd-muted">{{ $t("report_mobile.no_results") }}</span>
        </div>
        <div class="rd-pop__links">
          <template v-if="companyMode === 'multi'">
            <button type="button" class="ui-text-btn" @click="$emit('companies', companies.map((c) => c.id))">
              {{ $t("report_mobile.all") }}
            </button>
            <button type="button" class="ui-text-btn" @click="$emit('companies', [])">
              {{ $t("report_desktop.none") }}
            </button>
          </template>
          <button type="button" class="ui-text-btn rd-pop__done" @click="close(true)">
            {{ $t("report_desktop.done") }}
          </button>
        </div>
      </template>
    </ui-popover>

    <div v-if="showShifts" class="rd-shifts" role="group" :aria-label="$t('shifts')" :title="$t('shifts')">
      <button
        v-for="shift in allShifts"
        :key="shift"
        type="button"
        :data-shift="shift"
        :aria-pressed="String(shifts.includes(shift))"
        :aria-label="$t('report_mobile.shift_n', { n: shift })"
        :title="$t('report_mobile.shift_n', { n: shift })"
        @click="toggleShift(shift)"
      >
        {{ shift }}
      </button>
    </div>

    <slot name="filters"></slot>

    <label v-if="showSearch" class="rd-search">
      <v-icon size="16" color="#5f5852">mdi-magnify</v-icon>
      <span class="rd-visually-hidden">{{ searchPlaceholder || $t("search") }}</span>
      <input
        ref="search"
        type="search"
        :value="search"
        :placeholder="searchPlaceholder || $t('search')"
        @input="$emit('update:search', $event.target.value)"
      />
      <kbd>/</kbd>
    </label>

    <span class="rd-fbar__spacer"></span>

    <slot name="actions"></slot>

    <button
      type="button"
      class="ui-icon-btn"
      :class="{ 'is-spinning': loading }"
      :aria-label="$t('report_mobile.refresh')"
      :title="`${$t('report_mobile.refresh')} (r)`"
      @click="$emit('refresh')"
    >
      <v-icon size="18">mdi-refresh</v-icon>
    </button>
    <button
      v-if="canPrint"
      type="button"
      class="ui-icon-btn"
      :disabled="exportDisabled"
      :aria-label="$t('report_mobile.print_pdf')"
      :title="$t('report_mobile.print_pdf')"
      @click="$emit('print')"
    >
      <v-icon size="18">mdi-printer-outline</v-icon>
    </button>
    <button
      v-if="canExcel"
      type="button"
      class="ui-icon-btn"
      :disabled="exportDisabled"
      :aria-label="$t('report_mobile.download_excel')"
      :title="$t('report_mobile.download_excel')"
      @click="$emit('excel')"
    >
      <v-icon size="18" color="#15803d">mdi-microsoft-excel</v-icon>
    </button>
  </div>
</template>

<script>
import ReportCalendar from "../shared/ReportCalendar.vue";
import hasPlural from "../shared/hasPlural";
import { countDays, findPresetKey, formatRange, formatShortDate, normalizeRange } from "../shared/periods";

const ALL_SHIFTS = [1, 2, 3, 4];
const DEFAULT_QUICK_PRESETS = ["today", "tomorrow", "this_week"];

/**
 * Zajednička traka filtera izveštaja računara: period, firme, smene, pretraga i radnje.
 * Ne drži izbor: dobija vrednosti i javlja promene (roditelj ih upisuje u store i adresu).
 */
export default {
  name: "ReportFilterBar",
  mixins: [hasPlural],
  components: { ReportCalendar },
  props: {
    from: { type: String, required: true },
    to: { type: String, required: true },
    /** false = bez izbora perioda (ekran ga ne koristi) */
    showPeriod: { type: Boolean, default: true },
    /** period je prikazan, ali se ne menja (npr. stanje magacina je uvek za danas) */
    periodLocked: { type: Boolean, default: false },
    presets: { type: Array, default: () => [] },
    shifts: { type: Array, default: () => [...ALL_SHIFTS] },
    showShifts: { type: Boolean, default: false },
    /** Sve firme restorana: [{ id, name }] */
    companies: { type: Array, default: () => [] },
    selectedIds: { type: Array, default: () => [] },
    /** null = bez izbora firmi, "multi" = više firmi, "single" = jedna firma */
    companyMode: { type: String, default: null },
    search: { type: String, default: "" },
    showSearch: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    canExcel: { type: Boolean, default: true },
    canPrint: { type: Boolean, default: true },
    /** štampa i Excel su isključeni (npr. nema šta da se izveze) */
    exportDisabled: { type: Boolean, default: false },
    /** tekst u polju pretrage umjesto opšteg "Pretraga" (kad se traži nešto određeno) */
    searchPlaceholder: { type: String, default: "" },
    /** Koji brzi izbori perioda su u traci (ostali su u iskačućem izboru) */
    quickKeys: { type: Array, default: () => DEFAULT_QUICK_PRESETS },
    /** Nazivi za izbor stavki (firme ili restorani): { title, search, pick, all(n), count(n) } */
    labels: { type: Object, default: null },
  },
  data: () => ({
    draft: [],
    companyQuery: "",
    allShifts: ALL_SHIFTS,
  }),
  computed: {
    quickOptions() {
      return this.quickPresets.map((preset) => ({ value: preset.key, label: this.$t(`report_mobile.presets.${preset.key}`) }));
    },
    quickPresets() {
      return this.quickKeys.map((key) => this.presets.find((preset) => preset.key === key)).filter(Boolean);
    },
    activeKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    /** Brzi izbori su u traci pored, pa dugme pokazuje datum; ostali izbori (npr. prošli mesec) imaju naziv. */
    periodLabel() {
      if (this.activeKey && !this.quickKeys.includes(this.activeKey)) {
        const label = this.$t(`report_mobile.presets.${this.activeKey}`);
        return this.from === this.to ? `${label} · ${formatShortDate(this.from)}` : label;
      }
      return formatRange(this.from, this.to);
    },
    draftRange() {
      if (!this.draft.length) {
        return "";
      }
      return formatRange(this.draft[0], this.draft[1] || this.draft[0]);
    },
    draftText() {
      if (this.draft.length === 1) {
        return this.$t("report_mobile.pick_end_date");
      }
      const days = countDays(this.draft[0], this.draft[1]);
      return this.pluralT("report_mobile.days_n", days);
    },
    allSelected() {
      return this.companies.length > 0 && this.selectedIds.length === this.companies.length;
    },
    companyAccent() {
      return this.companyMode === "single" ? this.selectedIds.length > 0 : !this.allSelected;
    },
    selectedCompanies() {
      return this.companies.filter((company) => this.selectedIds.includes(company.id));
    },
    companiesLabel() {
      if (this.companyMode === "single") {
        return this.selectedCompanies[0]
          ? this.toLatin(this.selectedCompanies[0].name)
          : this.entityLabels.pick;
      }
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      if (this.allSelected) {
        return this.entityLabels.all(this.companies.length);
      }
      return this.entityLabels.count(this.selectedCompanies.length);
    },
    entityLabels() {
      return (
        this.labels || {
          title: this.$t("companies"),
          search: this.$t("report_mobile.search_companies"),
          pick: this.$t("select_company"),
          all: (n) => this.$t("report_mobile.all_companies_n", { n }),
          count: (n) => this.pluralT("report_mobile.companies_n", n),
        }
      );
    },
    visibleCompanies() {
      const query = this.companyQuery.trim();
      if (!query) {
        return this.companies;
      }
      return this.customSearch(
        this.companies.map((company) => ({ ...company, text: this.toLatin(company.name) })),
        [{ value: "text" }],
        query
      );
    },
  },
  watch: {
    from() {
      this.popovers().forEach((popover) => popover.close());
    },
  },
  methods: {
    pickQuick(key) {
      const preset = this.quickPresets.find((item) => item.key === key);
      if (preset) {
        this.$emit("period", { from: preset.from, to: preset.to });
      }
    },
    focusSearch() {
      const input = this.$refs.search;
      if (input) {
        input.focus();
        input.select();
      }
    },
    popovers() {
      return [this.$refs.period, this.$refs.companies].filter(Boolean);
    },
    /** Izbori su u istoj traci: otvaranjem jednog zatvara se drugi. */
    onOpen(name) {
      this.companyQuery = "";
      if (name === "period") {
        this.draft = this.from === this.to ? [this.from] : [this.from, this.to];
      }
      this.popovers().filter((popover) => popover !== this.$refs[name]).forEach((popover) => popover.close());
    },
    closeOnEscape(event) {
      const open = this.popovers().find((popover) => popover.open);
      if (open) {
        event.stopPropagation();
        open.close(true);
      }
    },
    applyPreset(preset) {
      this.$emit("period", { from: preset.from, to: preset.to });
      this.$refs.period.close(true);
    },
    pickDay(date) {
      if (this.draft.length !== 1 || date < this.draft[0]) {
        this.draft = [date];
        return;
      }
      this.draft = [this.draft[0], date];
      this.applyDraft();
    },
    applyDraft() {
      const [from, to] = normalizeRange(this.draft);
      this.$emit("period", { from, to });
      this.$refs.period.close(true);
    },
    toggleShift(shift) {
      const selected = this.shifts.includes(shift);
      if (selected && this.shifts.length === 1) {
        return;
      }
      const next = selected ? this.shifts.filter((item) => item !== shift) : [...this.shifts, shift];
      this.$emit("update:shifts", next.sort());
    },
    pickCompany(id) {
      if (this.companyMode === "single") {
        this.$emit("companies", [id]);
        this.$refs.companies.close(true);
        return;
      }
      const next = this.selectedIds.includes(id)
        ? this.selectedIds.filter((item) => item !== id)
        : [...this.selectedIds, id];
      this.$emit("companies", next);
    },
  },
};
</script>
